import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import EscalationControl from '../../customer/components/EscalationControl';
import { grantLabel } from '../utils/toolGrants';
import type { FailureRecord, WorkflowRun } from '../../../types/backend';

interface Props {
  failure: FailureRecord;
  run: WorkflowRun;
  label: string;
  /** The step's allowed_tools entries. */
  grants: string[];
  busy: boolean;
  onRetry: () => void;
  onEditWorkflow?: (workflowId: string) => void;
}

// A step that failed because the operator's access does not cover it. Nothing the agent does
// fixes that, and the workflow never escalates: the operator escalates themselves and retries,
// or changes the workflow so the step needs less.
const WorkflowAccessFailure: React.FC<Props> = ({
  failure,
  run,
  label,
  grants,
  busy,
  onRetry,
  onEditWorkflow,
}) => {
  const version = run.workflow_ref.version;
  return (
    <Alert severity="warning" aria-label={`Needs access: ${label}`}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        Needs access: {label}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
        {failure.error}
      </Typography>
      {grants.length > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
          This step uses: {grants.map((g) => `${grantLabel(g)} (${g})`).join(', ')}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
        <Typography variant="caption">Escalate:</Typography>
        <EscalationControl customerId={run.customer_id} />
        <Button size="small" variant="contained" color="warning" disabled={busy} onClick={onRetry}>
          Retry step
        </Button>
        {onEditWorkflow && (
          <Button
            size="small"
            color="inherit"
            disabled={busy}
            onClick={() => onEditWorkflow(run.workflow_ref.workflow_id)}
          >
            Edit workflow
          </Button>
        )}
      </Stack>
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {onEditWorkflow
            ? `Escalate, then retry. An edit lands on the draft: publish it and start a new run, since this run stays on v${version}.`
            : `Escalate, then retry. To change what this step needs, publish a new version of the workflow and start a new run; this run stays on v${version}.`}
        </Typography>
      </Box>
    </Alert>
  );
};

export default WorkflowAccessFailure;
